import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { config } from '../config';

const SSOPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleSSO = async () => {
      try {
        // Get SSO token from URL
        const ssoToken = searchParams.get('token');

        if (!ssoToken) {
          setError('No SSO token provided. Please try again from the admin portal.');
          setLoading(false);
          return;
        }

        // Validate SSO token with backend and get Firebase Custom Token
        const response = await fetch(`${config.server.backendUrl}/api/auth/validate-sso-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ssoToken }),
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Failed to validate SSO token');
        }

        const { firebaseToken, user } = data.data;

        // Sign in to Firebase with the Custom Token
        const auth = getAuth();
        await signInWithCustomToken(auth, firebaseToken);

        console.log('SSO authentication successful:', user);

        // Check for mode and sessionId parameters for training mode
        const mode = searchParams.get('mode');
        const sessionId = searchParams.get('sessionId');
        const trackId = searchParams.get('trackId');

        // Determine redirect path based on mode
        let redirectPath = '/conversation'; // Default for Live Call

        if (mode === 'training' && sessionId && trackId) {
          redirectPath = `/training/session/${sessionId}?trackId=${encodeURIComponent(trackId)}`;
          console.log('🎓 Redirecting to training session:', { sessionId, trackId });
        }

        // Clear URL parameters (remove SSO token from URL for security)
        window.history.replaceState({}, '', redirectPath);

        // Redirect to appropriate page
        navigate(redirectPath, { replace: true });
      } catch (err: any) {
        console.error('SSO authentication failed:', err);
        setError(err.message || 'Failed to authenticate. Please try again.');
        setLoading(false);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    };

    handleSSO();
  }, [searchParams, navigate]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="background.default"
      p={3}
    >
      {loading && !error && (
        <>
          <CircularProgress size={60} />
          <Typography variant="h6" mt={3}>
            Authenticating...
          </Typography>
          <Typography variant="body2" color="textSecondary" mt={1}>
            Please wait while we securely log you in
          </Typography>
        </>
      )}

      {error && (
        <Box maxWidth="sm" width="100%">
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Authentication Failed
            </Typography>
            <Typography variant="body2">{error}</Typography>
            <Typography variant="body2" mt={1}>
              Redirecting to login page...
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default SSOPage;
